import { cn } from "@/lib/utils";
import {
  MAX_ZOOM,
  MIN_ZOOM,
  useCanvasPosRealtime,
  useSelectedNavTreeNode,
  useMultiselectedNavTreeNode,
  useCanvasStore,
} from "@/components/Canvas/stores/canvasStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { SimpleCanvasRef } from "../SimpleCanvas/SimpleCanvas";
import { useActiveCanvas, useActiveMainCanvas } from "@/utils/utils";
import {
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  MinusIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useZoomShortcuts } from "@/hooks/keyboard-shortcuts";
import { computeCenteredViewport } from "@/components/Canvas/hooks/useCanvasViewportControl";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { findElementByDomUniquePath } from "@/components/Canvas/utils/utils";
import {
  TEMPO_ELEMENT_UNKNOWN_WIDTH_DEFAULT,
  TEMPO_ELEMENT_UNKNOWN_HEIGHT_DEFAULT,
} from "@/components/Canvas/utils/canvasClipboardData";

// Zoom buffer factors for different zoom operations
const ZOOM_TO_FIT_BUFFER = 0.95;
const ZOOM_TO_SELECTION_BUFFER = 0.9;
const ZOOM_TO_STORYBOARD_BUFFER = 0.85;

/**
 * Function to find the nearest power of 2 for smooth zoom transitions
 */
const nearestPowerOf2 = (number: number) => {
  return Math.pow(2, Math.round(Math.log(number) / Math.log(2)));
};

/**
 * Calculates the bounding rectangle for a selected element within its storyboard
 */
const getStoryboardRectForElement = (
  element: any,
  applicationStoryboards: Record<
    string,
    { iframeId: string; contentWindow: WindowProxy | null }
  >,
  activeCanvas: any,
): { x: number; y: number; width: number; height: number } | null => {
  try {
    // Only application storyboards have accessible DOM in their iframes
    const appStoryboard = applicationStoryboards[element.storyboardId];
    if (!appStoryboard?.contentWindow) return null;

    const domElement = findElementByDomUniquePath(
      appStoryboard.contentWindow.document,
      element.uniquePath,
    );
    if (!domElement) return null;

    const iframeElement = document.getElementById(
      appStoryboard.iframeId,
    ) as HTMLIFrameElement;
    if (!iframeElement) return null;

    const rect = domElement.getBoundingClientRect();
    const iframeRect = iframeElement.getBoundingClientRect();
    const storyboard = activeCanvas.storyboards?.find(
      (sb: any) => sb.id === element.storyboardId,
    );
    if (!storyboard) return null;

    return {
      x: (storyboard.x || 0) + (rect.left - iframeRect.left),
      y: (storyboard.y || 0) + (rect.top - iframeRect.top),
      width: rect.width,
      height: rect.height,
    };
  } catch {
    return null;
  }
};

interface ZoomControlsV2Props {
  simpleCanvasRef: React.RefObject<SimpleCanvasRef>;
}

/**
 * Zoom controls component that provides zoom in/out/reset functionality,
 * keyboard shortcuts, and fullscreen toggle for the canvas.
 */
const ZoomControlsV2 = ({ simpleCanvasRef }: ZoomControlsV2Props) => {
  const [internalZoomPerc, setInternalZoomPerc] = useState("100%");

  // Canvas state
  const activeCanvas = useActiveCanvas();
  const { zoomPerc } = useCanvasPosRealtime();
  const setCanvasPosValue = useCanvasStore((state) => state.setCanvasPosValue);

  // Selection state
  const { selectedElement } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();

  // Layout state for fullscreen functionality
  const leftExpanded = useLayoutStore((state) => state.leftExpanded);
  const rightExpanded = useLayoutStore((state) => state.rightExpanded);
  const expandLeft = useLayoutStore((state) => state.expandLeft);
  const expandRight = useLayoutStore((state) => state.expandRight);
  const collapseLeft = useLayoutStore((state) => state.collapseLeft);
  const collapseRight = useLayoutStore((state) => state.collapseRight);

  const zoom = zoomPerc || 1;
  const canFullscreen = leftExpanded || rightExpanded;

  // ================================================================================
  // EVENT HANDLERS
  // ================================================================================

  const handleZoomIn = useCallback(() => {
    if (activeCanvas?.id) {
      const newZoom = nearestPowerOf2(zoom * 2 * 128) / 128;
      simpleCanvasRef.current?.setZoom(newZoom);
    }
  }, [activeCanvas?.id, zoom, simpleCanvasRef]);

  const handleZoomOut = useCallback(() => {
    if (zoom && activeCanvas?.id) {
      const newZoom = nearestPowerOf2((zoom / 2) * 128) / 128;
      simpleCanvasRef.current?.setZoom(newZoom);
    }
  }, [zoom, activeCanvas?.id, simpleCanvasRef]);

  const handleZoomReset = useCallback(() => {
    if (activeCanvas?.id) {
      simpleCanvasRef.current?.setZoom(1);
    }
  }, [activeCanvas?.id, simpleCanvasRef]);

  const handleZoomInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalZoomPerc(e.currentTarget.value);
    },
    [],
  );

  const handleZoomInputBlur = useCallback(() => {
    const newVal = parseInt(internalZoomPerc.replace(/\D/g, ""));
    if (!Number.isNaN(newVal) && activeCanvas?.id) {
      setCanvasPosValue(activeCanvas.id, {
        zoomPerc: newVal / 100,
      });
      simpleCanvasRef.current?.setZoom(newVal / 100);
    }
  }, [internalZoomPerc, activeCanvas?.id, setCanvasPosValue, simpleCanvasRef]);

  const handleZoomInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.select();
    },
    [],
  );

  const handleZoomInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.currentTarget.blur();
      }
    },
    [],
  );

  const handleFullscreenToggle = useCallback(() => {
    if (canFullscreen) {
      collapseLeft();
      collapseRight();
    } else {
      expandLeft();
      expandRight();
    }
  }, [canFullscreen, collapseLeft, collapseRight, expandLeft, expandRight]);

  const handleZoomToFit = useCallback(() => {
    if (!activeCanvas?.id || !simpleCanvasRef.current) return;

    const visibleStoryboards = (activeCanvas.storyboards || []).filter(
      (sb) => !sb.deleted,
    );

    if (visibleStoryboards.length === 0) {
      simpleCanvasRef.current.setCanvasTransform({ x: 0, y: 0, zoom: 1 });
      return;
    }

    const canvasElement = simpleCanvasRef.current.getCanvasRef();
    if (!canvasElement) return;

    const items = visibleStoryboards.map((sb) => ({
      x: sb.x || 0,
      y: sb.y || 0,
      width: sb.width || TEMPO_ELEMENT_UNKNOWN_WIDTH_DEFAULT,
      height: sb.height || TEMPO_ELEMENT_UNKNOWN_HEIGHT_DEFAULT,
    }));

    const viewport = computeCenteredViewport(
      items,
      canvasElement.clientWidth,
      canvasElement.clientHeight,
      ZOOM_TO_FIT_BUFFER,
      null,
    );
    simpleCanvasRef.current.setCanvasTransform(viewport);
  }, [activeCanvas, simpleCanvasRef]);

  const handleZoomToSelection = useCallback(() => {
    if (!activeCanvas?.id || !simpleCanvasRef.current) return;

    const canvasElement = simpleCanvasRef.current.getCanvasRef();
    if (!canvasElement) {
      handleZoomToFit();
      return;
    }

    const selectedElements =
      multiselectedElements.length > 0
        ? multiselectedElements
        : selectedElement && !selectedElement.isEmpty()
          ? [selectedElement]
          : [];

    if (selectedElements.length === 0) {
      handleZoomToFit();
      return;
    }

    const storyboardElements = selectedElements.filter((element) =>
      element.isStoryboard(),
    );

    if (storyboardElements.length > 0) {
      simpleCanvasRef.current.centerViewOnItems(
        storyboardElements.map((element) => element.storyboardId),
        ZOOM_TO_STORYBOARD_BUFFER,
      );
      return;
    }

    const applicationStoryboards =
      useIframePoolStore.getState().applicationStoryboards;
    const itemRects = selectedElements
      .map((element) =>
        getStoryboardRectForElement(
          element,
          applicationStoryboards,
          activeCanvas,
        ),
      )
      .filter(
        (
          item,
        ): item is { x: number; y: number; width: number; height: number } =>
          item !== null,
      );

    if (itemRects.length > 0) {
      const viewport = computeCenteredViewport(
        itemRects,
        canvasElement.clientWidth,
        canvasElement.clientHeight,
        ZOOM_TO_SELECTION_BUFFER,
        null,
      );
      simpleCanvasRef.current.setCanvasTransform(viewport);
    } else {
      const storyboardIds = [
        ...new Set(selectedElements.map((element) => element.storyboardId)),
      ];
      if (storyboardIds.length > 0) {
        simpleCanvasRef.current.centerViewOnItems(
          storyboardIds,
          ZOOM_TO_STORYBOARD_BUFFER,
        );
      } else {
        handleZoomToFit();
      }
    }
  }, [
    activeCanvas,
    simpleCanvasRef,
    selectedElement,
    multiselectedElements,
    handleZoomToFit,
  ]);

  // Keyboard shortcuts for zoom
  useZoomShortcuts({
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    zoomReset: handleZoomReset,
    zoomToFit: handleZoomToFit,
    zoomToSelection: handleZoomToSelection,
  });

  // ================================================================================
  // RENDER
  // ================================================================================

  // Update zoom display when zoom percentage changes
  useEffect(() => {
    if (!zoom) return;

    const formattedZoom = Math.round(zoom * 100) + "%";
    setInternalZoomPerc(formattedZoom);
  }, [zoom]);

  const renderFullscreenControl = () => (
    <div className="bg-background-tertiary shadow-md text-white flex flex-row text-xs rounded-sm divide-x divide-secondary-200 overflow-hidden">
      <div
        className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
        onClick={handleFullscreenToggle}
      >
        {canFullscreen ? <EnterFullScreenIcon /> : <ExitFullScreenIcon />}
      </div>
    </div>
  );

  const renderZoomControls = () => (
    <div className="bg-background-tertiary shadow-md text-white flex flex-row text-xs rounded-sm divide-x divide-secondary-200 overflow-hidden">
      {/* Zoom Out Button */}
      <div
        className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
        onClick={handleZoomOut}
      >
        <MinusIcon />
      </div>

      {/* Zoom Percentage Input */}
      <div className="w-12 flex justify-center items-center cursor-pointer h-7">
        <input
          className="w-full h-full bg-transparent focus:outline-hidden py-1 text-center text-2xs focus-visible::ring-1 focus-visible:ring-ring focus-visible:outline-hidden focus:border focus:border-primary-0 hover:bg-background-accent"
          value={internalZoomPerc}
          onChange={handleZoomInputChange}
          onBlur={handleZoomInputBlur}
          onFocus={handleZoomInputFocus}
          onKeyDown={handleZoomInputKeyDown}
        />
      </div>

      {/* Zoom In Button */}
      <div
        className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7"
        onClick={handleZoomIn}
      >
        <PlusIcon />
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "flex justify-end items-center pointer-events-auto absolute left-6 bottom-6",
      )}
    >
      <div className="flex flex-row space-x-1 text-gray-600">
        {/* Fullscreen Toggle Control */}
        {renderFullscreenControl()}

        {/* Zoom Controls */}
        {renderZoomControls()}
      </div>
    </div>
  );
};

export default ZoomControlsV2;
